/**
 * Rekurzivní model layoutu (page-builder). Čistý modul — bez server/klient importů, jen zod + typy.
 * `LayoutNode` = discriminated union (kind): widget | container | grid | tabs. Rekurze přes z.lazy.
 * Strom ⇒ cykly nemožné. Meze (hloubka/počet uzlů/unikátní id), které zod neumí deklarativně,
 * vynucuje `validateLayout()` jedním průchodem po safeParse.
 */

import { z } from "zod";

export const LAYOUT_SCHEMA_VERSION = 1 as const;
export const MAX_DEPTH = 8; // root = hloubka 0
export const MAX_NODES = 400; // strop na celý strom (perf/DoS)
export const MAX_CHILDREN = 60; // per kontejner

// ── strany (margin/padding): číslo = všechny strany, nebo per-strana {t,r,b,l} ──
export type Sides = { t?: number; r?: number; b?: number; l?: number };
const sideNum = z.number().int().min(0).max(256);
export const sidesValueSchema = z.union([
  sideNum,
  z.object({ t: sideNum.optional(), r: sideNum.optional(), b: sideNum.optional(), l: sideNum.optional() }).strict(),
]);

// ── size: jak se uzel chová ve flex rodiči ──
export const sizeSchema = z
  .object({
    grow: z.number().min(0).max(64).optional(), // flex-grow
    basis: z
      .union([z.literal("auto"), z.string().regex(/^\d{1,3}(\.\d+)?%$/), z.string().regex(/^\d{1,5}px$/)])
      .optional(), // flex-basis
    minW: z.number().int().min(0).max(4000).optional(),
    minH: z.number().int().min(0).max(4000).optional(),
    height: z.union([z.literal("auto"), z.number().int().min(20).max(4000)]).optional(), // px výška u fixních uzlů
    margin: sidesValueSchema.optional(), // px mezera vně slotu (číslo nebo per-strana)
    padding: sidesValueSchema.optional(), // px mezera uvnitř slotu (číslo nebo per-strana)
  })
  .strict();
export type LayoutSize = z.infer<typeof sizeSchema>;

const idSchema = z.string().min(1).max(40);
const alignSchema = z.enum(["start", "center", "end", "stretch", "baseline"]); // cross-axis
const justifySchema = z.enum(["start", "center", "end", "between", "around", "evenly"]); // main-axis

export type LayoutDir = "row" | "col";
export type LayoutAlign = z.infer<typeof alignSchema>;
export type LayoutJustify = z.infer<typeof justifySchema>;

// ── TS typy (deklarované předem kvůli z.lazy) ──
export type WidgetNode = { kind: "widget"; id: string; type: string; config: Record<string, unknown>; size?: LayoutSize };
export interface ContainerNode {
  kind: "container";
  id: string;
  dir: LayoutDir;
  gap: number;
  padding: number | Sides;
  align: LayoutAlign;
  justify: LayoutJustify;
  wrap: boolean;
  stackOnMobile?: boolean;
  children: LayoutNode[];
  size?: LayoutSize;
}
export interface GridNode {
  kind: "grid";
  id: string;
  cols: number;
  /** Responzivní režim: když je nastaveno, sloupce se počítají z minimální šířky buňky
   *  (`repeat(auto-fill, minmax(minColW, 1fr))`) → tabulka přetéká podle šířky obrazovky
   *  (2/3/4 sloupce jako device overview). `cols` slouží jen jako fallback / strop colSpanu. */
  minColW?: number;
  rowGap: number;
  colGap: number;
  children: { node: LayoutNode; colSpan: number; rowStart?: number }[];
  size?: LayoutSize;
}
export interface TabsNode {
  kind: "tabs";
  id: string;
  tabs: { id: string; label: string; child: LayoutNode }[];
  size?: LayoutSize;
}
export type LayoutNode = WidgetNode | ContainerNode | GridNode | TabsNode;

const widgetNodeSchema = z.object({
  kind: z.literal("widget"),
  id: idSchema,
  type: z.string().min(1).max(64),
  config: z.record(z.string(), z.unknown()).default({}),
  size: sizeSchema.optional(),
});

export const layoutNodeSchema: z.ZodType<LayoutNode> = z.lazy(() =>
  z.discriminatedUnion("kind", [
    widgetNodeSchema,
    z.object({
      kind: z.literal("container"),
      id: idSchema,
      dir: z.enum(["row", "col"]),
      gap: z.number().int().min(0).max(64).default(8),
      padding: sidesValueSchema.default(0),
      align: alignSchema.default("stretch"),
      justify: justifySchema.default("start"),
      wrap: z.boolean().default(false),
      stackOnMobile: z.boolean().optional(),
      children: z.array(layoutNodeSchema).max(MAX_CHILDREN).default([]),
      size: sizeSchema.optional(),
    }),
    z.object({
      kind: z.literal("grid"),
      id: idSchema,
      cols: z.number().int().min(1).max(24),
      minColW: z.number().int().min(40).max(1000).optional(),
      rowGap: z.number().int().min(0).max(64).default(8),
      colGap: z.number().int().min(0).max(64).default(8),
      children: z
        .array(z.object({ node: layoutNodeSchema, colSpan: z.number().int().min(1).max(24), rowStart: z.number().int().min(1).optional() }))
        .max(MAX_CHILDREN)
        .default([]),
      size: sizeSchema.optional(),
    }),
    z.object({
      kind: z.literal("tabs"),
      id: idSchema,
      tabs: z.array(z.object({ id: idSchema, label: z.string().max(60), child: layoutNodeSchema })).min(1).max(16),
      size: sizeSchema.optional(),
    }),
  ])
);

/** Root je VŽDY container (invariant rendereru i editoru). */
export const layoutDocSchema = z.object({
  schemaVersion: z.literal(LAYOUT_SCHEMA_VERSION),
  root: layoutNodeSchema.refine((n) => n.kind === "container", "root musí být container"),
});
export type LayoutDoc = z.infer<typeof layoutDocSchema>;

// ── helpery (sdílené rendererem i editorem) ──

/** Přímí potomci uzlu (sjednocené napříč kind). */
export const childrenOf = (n: LayoutNode): LayoutNode[] => {
  if (n.kind === "container") return n.children;
  if (n.kind === "grid") return n.children.map((c) => c.node);
  if (n.kind === "tabs") return n.tabs.map((t) => t.child);
  return [];
};

/** DFS návštěva všech uzlů. */
export const walk = (n: LayoutNode, visit: (node: LayoutNode, depth: number) => void, depth = 0): void => {
  visit(n, depth);
  for (const c of childrenOf(n)) walk(c, visit, depth + 1);
};

export const countNodes = (n: LayoutNode): number => {
  let c = 0;
  walk(n, () => c++);
  return c;
};

export const collectIds = (n: LayoutNode): string[] => {
  const ids: string[] = [];
  walk(n, (node) => ids.push(node.id));
  return ids;
};

export const maxDepth = (n: LayoutNode): number => {
  let d = 0;
  walk(n, (_node, depth) => {
    if (depth > d) d = depth;
  });
  return d;
};

export type LayoutValidation = { ok: true; doc: LayoutDoc } | { ok: false; errors: string[] };

/**
 * Plná validace: zod + meze, které zod neumí (hloubka, počet uzlů, unikátní id). Volá ji server
 * (PUT) i klient (load); nevalidní → volající fallbackuje na default.
 */
export const validateLayout = (input: unknown): LayoutValidation => {
  const parsed = layoutDocSchema.safeParse(input);
  if (!parsed.success) return { ok: false, errors: parsed.error.issues.map((i) => `${i.path.join(".")}: ${i.message}`) };
  const doc = parsed.data;
  const errors: string[] = [];
  const n = countNodes(doc.root);
  if (n > MAX_NODES) errors.push(`příliš mnoho uzlů (${n} > ${MAX_NODES})`);
  const d = maxDepth(doc.root);
  if (d > MAX_DEPTH) errors.push(`příliš hluboké vnoření (${d} > ${MAX_DEPTH})`);
  const ids = collectIds(doc.root);
  if (new Set(ids).size !== ids.length) errors.push("duplicitní id uzlů");
  return errors.length ? { ok: false, errors } : { ok: true, doc };
};
